import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useResponsiveLayout } from '../hooks/ui/useResponsiveLayout';
import { roleHomePath } from '../utils/ui/roleHome';
import LogJobSkeleton from './LogJobSkeleton';
import UnscheduledLogJobHeader from '../components/worker/unscheduledVisit/UnscheduledLogJobHeader';
import { UNSCHEDULED_FLOW_STATE_KEY, readIdentitySeed } from '../components/worker/unscheduledVisit/unscheduledFlowShared';
import UnscheduledRailIdentity from '../components/worker/unscheduledVisit/UnscheduledRailIdentity';
import { UNSCHEDULED_RAIL_RAISED } from '../components/worker/unscheduledVisit/unscheduledPush';

// Leaf-route chunk-load fallback for /log-job/:customerId. Sits BELOW the boot-splash
// Suspense so that while the LogJob JS chunk downloads, the user sees a layout
// skeleton (looks like the page is opening) instead of the CrewVex wordmark. The
// chunk is also prefetched from the boards (prefetchLogJobChunk), so in practice
// this rarely renders past the first cold open.
//
// KAN-765: entered from the unscheduled-visit flow, it wears the SAME header as the
// flow (and as Log Job itself), so the frame never shows the board between the
// flow unmounting and Log Job's first paint.
export default function LogJobRouteFallback() {
  const { isMobile, isTablet } = useResponsiveLayout();
  const { role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const layout = isMobile ? 'phone' : isTablet ? 'tablet' : 'desktop';
  if (!location.state?.[UNSCHEDULED_FLOW_STATE_KEY]) return <LogJobSkeleton layout={layout} />;
  const params = new URLSearchParams(location.search);
  // Desktop (KAN-771): the rail shows the same identity card the Reason step and Log Job draw.
  const seed = layout === 'desktop' ? readIdentitySeed(location.state) : null;
  return (
    <LogJobSkeleton
      layout={layout}
      identityNode={seed ? <UnscheduledRailIdentity name={seed.name} address={seed.address} offSeason={seed.offSeason} /> : null}
      railStyle={seed ? UNSCHEDULED_RAIL_RAISED : null}
      headerNode={(
        <UnscheduledLogJobHeader
          layout={layout}
          customerId={customerId}
          reason={params.get('reason') || ''}
          sameDayOverride={params.get('sameDayOverride') === '1'}
          returnTo={location.state?.returnTo || roleHomePath(role)}
          navigate={navigate}
        />
      )}
    />
  );
}
